import { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BULK_CAMPAIGNS_DROP_FORMATS,
  BULK_CAMPAIGNS_DROP_TITLE,
  BULK_CAMPAIGNS_FILE_READY,
  BULK_CAMPAIGNS_REPLACE_FILE,
} from "../../../i18n/keysTranslations";
import { BULK_ACCEPT_ATTRIBUTE } from "./bulkCampaignsConstants";

const BulkCampaignsDropzone = ({
  disabled = false,
  fileName = "",
  fileSizeLabel = "",
  onFileSelected,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const hasFile = Boolean(fileName);

  const openFilePicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      onFileSelected(file);
    }
  };

  const onInputChange = (event) => {
    const file = event.target.files?.[0];
    // Reset so the same file can be selected again
    event.target.value = "";
    if (file) {
      onFileSelected(file);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={openFilePicker}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openFilePicker();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        py: 5,
        px: 3,
        textAlign: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        borderRadius: 2,
        border: `2px dashed ${
          isDragging ? theme.palette.primary.main : theme.palette.grey[400]
        }`,
        backgroundColor: isDragging
          ? theme.palette.action.hover
          : "transparent",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": disabled ? {} : { borderColor: theme.palette.primary.main },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={BULK_ACCEPT_ATTRIBUTE}
        onChange={onInputChange}
      />
      <UploadFileOutlinedIcon sx={{ fontSize: 40, color: "text.secondary" }} />
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {hasFile
          ? fileSizeLabel
            ? t(BULK_CAMPAIGNS_FILE_READY, {
                fileName,
                size: fileSizeLabel,
              })
            : fileName
          : t(BULK_CAMPAIGNS_DROP_TITLE)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {hasFile
          ? t(BULK_CAMPAIGNS_REPLACE_FILE)
          : t(BULK_CAMPAIGNS_DROP_FORMATS)}
      </Typography>
    </Box>
  );
};

export default BulkCampaignsDropzone;
